import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaCamera, FaTimes, FaReceipt, FaSpinner, FaCheck, FaLightbulb } from 'react-icons/fa';
import BottomNavigation from './BottomNavigation';
import { useAuth } from '../security/security/AuthContext';
import axios from 'axios';
import HomeToastNotification from './auth/loaders/HomeToastNotification';
import WhatsAppFloat from './auth/WhatsAppFloat';

const Proof = () => {
  const [currentView, setCurrentView] = useState('main');
  const [description, setDescription] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading true to always show loading first
  const { userDetails, token, updateUserDetails } = useAuth();
  const [toastNotification, setToastNotification] = useState(null);

  const refreshUserData = async () => {
    try {
      const userResponse = await axios.get(`https://api.visiontech2030.com/api/v1/auth/${userDetails.email}`, {
        headers: {
          'Authorization': token
        }
      });
      updateUserDetails(userResponse.data);
    } catch (error) {
      console.error('Error refreshing user data:', error);
      setToastNotification({ message: 'Failed to refresh user data', type: 'error' });
    }
  };

  const fetchProofs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://api.visiontech2030.com/api/proofs/approved');
      // Sort the proofs in descending order by ID before setting state
      const sortedProofs = response.data.sort((a, b) => b.id - a.id);
      setProofs(sortedProofs);
      setTimeout(() => {
        setLoading(false);
      }, 1000); // Short delay to ensure loading state is always shown
    } catch (error) {
      console.error('Error fetching proofs:', error);
      setToastNotification({
        message: 'Failed to fetch proofs. Please try again.',
        type: 'error'
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    // Refresh user data when component mounts
    refreshUserData();
    fetchProofs();
  }, []);

  useEffect(() => {
    if (currentView === 'main') {
      refreshUserData();
    }
  }, [currentView]);

  const handleImageSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedImage) {
      setToastNotification({
        message: 'Please select an image',
        type: 'error'
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('image', selectedImage);
    formData.append('description', description);
    formData.append('phoneNumber', phoneNumber);
    formData.append('userEmail', userDetails.email);

    try {
      await axios.post('https://api.visiontech2030.com/api/proofs/add', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setToastNotification({
        message: 'Proof submitted successfully!',
        type: 'success'
      });
      setCurrentView('main');
      setDescription('');
      setPhoneNumber('');
      setSelectedImage(null);
      fetchProofs(); // Refresh the proofs after submitting
    } catch (error) {
      console.error('Error submitting proof:', error);
      setToastNotification({
        message: 'Failed to submit proof. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const ProofItem = ({ proof }) => (
    <div className="improved-vision-tech-proof-item">
      <div className="improved-vision-tech-proof-item-image-container">
        <img 
          src={`https://api.visiontech2030.com${proof.imageUrl}`} 
          alt="Proof" 
          className="improved-vision-tech-proof-item-image" 
        />
        <div className="improved-vision-tech-proof-item-verified">
          <FaCheck />
          <span>Verified</span>
        </div>
      </div>
      <div className="improved-vision-tech-proof-item-details">
        <p className="improved-vision-tech-proof-item-description">{proof.description}</p>
        <div className="improved-vision-tech-proof-item-footer">
          <p className="improved-vision-tech-proof-item-phone">
            {proof.phoneNumber.replace(/(\d{3})\d{4}(\d{3})/, '$1****$2')}
          </p>
          <span className="improved-vision-tech-proof-item-date">
            {new Date(proof.createdAt || Date.now()).toLocaleDateString()}
          </span>
        </div>
      </div>
    </div>
  );

  const LoadingState = () => (
    <div className="improved-vision-tech-proof-loading">
      <div className="improved-vision-tech-proof-loader">
        <div className="improved-vision-tech-proof-spinner"></div>
      </div>
      <p>Loading transaction proofs...</p>
    </div>
  );

  const renderMainView = () => (
    <div className="improved-vision-tech-proof-container">
      <div className="improved-vision-tech-proof-header">
        <h1 className="improved-vision-tech-proof-title">Payment Verifications</h1>
        <div className="improved-vision-tech-proof-reward-banner">
          <div className="improved-vision-tech-proof-reward-icon">
            <FaLightbulb />
          </div>
          <p>Share your withdrawal proof and earn <strong>500+ cash bonus</strong></p>
        </div>
      </div>
      
      {loading ? (
        <LoadingState />
      ) : (
        <>
          <div className="improved-vision-tech-proof-action-bar">
            <button 
              className="improved-vision-tech-proof-share-button" 
              onClick={() => setCurrentView('share')}
            >
              <span className="improved-vision-tech-proof-button-icon">+</span>
              Share Your Proof
            </button>
            <div className="improved-vision-tech-proof-counter">
              <span>19k+</span> proofs verified
            </div>
          </div>
          
          <div className="improved-vision-tech-proof-list">
            {proofs.map(proof => (
              <ProofItem key={proof.id} proof={proof} />
            ))}
          </div>
          
          <div className="improved-vision-tech-proof-floating-button" onClick={() => setCurrentView('share')}>
            <span>+</span>
          </div>
        </>
      )}
    </div>
  );

  const renderShareProofView = () => (
    <div className="improved-vision-tech-proof-container">
      <div className="improved-vision-tech-proof-share-header">
        <button 
          className="improved-vision-tech-proof-back-button" 
          onClick={() => setCurrentView('main')}
        >
          <FaArrowLeft /> Back
        </button>
        <h2 className="improved-vision-tech-proof-share-title">Share Payment Proof</h2>
      </div>
      
      <div className="improved-vision-tech-proof-share-instructions">
        <div className="improved-vision-tech-proof-instruction-icon">
          <FaReceipt />
        </div>
        <p>Share your transaction screenshot to get <strong>500+ bonus</strong> and help others verify our platform's legitimacy.</p>
      </div>
      
      <form onSubmit={handleSubmit} className="improved-vision-tech-proof-form">
        <div className="improved-vision-tech-proof-form-group">
          <label htmlFor="description">Your Experience</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Share details about your transaction experience..."
            rows="3"
            required
          />
        </div>
        
        <div className="improved-vision-tech-proof-form-group">
          <label htmlFor="phoneNumber">Phone Number</label>
          <input
            type="tel"
            id="phoneNumber"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="Enter your phone number"
            required
          />
        </div>
        
        <div className="improved-vision-tech-proof-upload-area">
          <input
            type="file"
            id="proofImage"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
            required
          />
          <label htmlFor="proofImage" className="improved-vision-tech-proof-upload-label">
            {selectedImage ? (
              <div className="improved-vision-tech-proof-preview">
                <img src={URL.createObjectURL(selectedImage)} alt="Selected" />
                <button 
                  type="button" 
                  className="improved-vision-tech-proof-remove-image" 
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedImage(null);
                  }}
                >
                  <FaTimes />
                </button>
              </div>
            ) : (
              <div className="improved-vision-tech-proof-upload-placeholder">
                <FaCamera className="improved-vision-tech-proof-camera-icon" />
                <p>Tap to upload payment screenshot</p>
                <span>Maximum file size: 5MB</span>
              </div>
            )}
          </label>
        </div>
        
        <button 
          type="submit" 
          className="improved-vision-tech-proof-submit-button" 
          disabled={loading}
        >
          {loading ? (
            <>
              <FaSpinner className="improved-vision-tech-proof-spin-icon" /> 
              Submitting...
            </>
          ) : 'Submit Proof'}
        </button>
      </form>
    </div>
  );

  return (
    <div className="improved-vision-tech-proof-page">
      {currentView === 'main' ? renderMainView() : renderShareProofView()}
      <BottomNavigation />
      {toastNotification && (
        <HomeToastNotification
          message={toastNotification.message}
          type={toastNotification.type}
          onClose={() => setToastNotification(null)}
        />
      )}
      <WhatsAppFloat />
      
      <style jsx>{`
        .improved-vision-tech-proof-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #0c1631 0%, #1a2b6d 100%);
          color: #fff;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          padding-bottom: 80px;
        }
        
        .improved-vision-tech-proof-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px 16px;
          position: relative;
        }
        
        /* Header Styles */
        .improved-vision-tech-proof-header {
          margin-bottom: 24px;
        }
        
        .improved-vision-tech-proof-title {
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 16px;
          background: linear-gradient(90deg, #fff, #a5b4fc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          letter-spacing: 0.5px;
        }
        
        .improved-vision-tech-proof-reward-banner {
          background: rgba(255, 255, 255, 0.07);
          border-radius: 12px;
          padding: 14px;
          border-left: 4px solid #4f46e5;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(10px);
        }
        
        .improved-vision-tech-proof-reward-banner p {
          margin: 0;
          font-size: 14px;
          line-height: 1.5;
          color: rgba(255, 255, 255, 0.9);
        }
        
        .improved-vision-tech-proof-reward-banner strong {
          color: #a5b4fc;
          font-weight: 600;
        }
        
        .improved-vision-tech-proof-reward-icon {
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: #fff;
          flex-shrink: 0;
        }
        
        /* Action Bar Styles */
        .improved-vision-tech-proof-action-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        
        .improved-vision-tech-proof-share-button {
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          border: none;
          color: #fff;
          padding: 10px 18px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }
        
        .improved-vision-tech-proof-share-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(79, 70, 229, 0.4);
        }
        
        .improved-vision-tech-proof-button-icon {
          font-size: 16px;
          font-weight: bold;
        }
        
        .improved-vision-tech-proof-counter {
          background: rgba(255, 255, 255, 0.1);
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.8);
        }
        
        .improved-vision-tech-proof-counter span {
          font-weight: 600;
          color: #a5b4fc;
        }
        
        /* Loading State Styles */
        .improved-vision-tech-proof-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
        }
        
        .improved-vision-tech-proof-loader {
          margin-bottom: 16px;
        }
        
        .improved-vision-tech-proof-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(165, 180, 252, 0.3);
          border-top: 3px solid #a5b4fc;
          border-radius: 50%;
          animation: improved-vision-tech-proof-spin 1s linear infinite;
        }
        
        @keyframes improved-vision-tech-proof-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .improved-vision-tech-proof-loading p {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.8);
          margin: 0;
        }
        
        /* Proof List Styles */
        .improved-vision-tech-proof-list {
          display: grid;
          gap: 16px;
        }
        
        .improved-vision-tech-proof-item {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.08);
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          display: flex;
          flex-direction: column;
        }
        
        .improved-vision-tech-proof-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          background: rgba(255, 255, 255, 0.08);
        }
        
        .improved-vision-tech-proof-item-image-container {
          position: relative;
          width: 100%;
          height: auto;  /* Changed from fixed height */
          overflow: visible;  /* Changed from hidden */
        }

        .improved-vision-tech-proof-item-image {
          width: 100%;
          height: auto;  /* Changed from 100% */
          object-fit: contain;  /* Changed from cover */
          transition: transform 0.5s ease;
        }
        
        .improved-vision-tech-proof-item:hover .improved-vision-tech-proof-item-image {
          transform: scale(1.03);
        }
        
        .improved-vision-tech-proof-item-verified {
          position: absolute;
          top: 12px;
          right: 12px;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          font-size: 12px;
          font-weight: 500;
          border-radius: 20px;
          padding: 4px 10px;
          display: flex;
          align-items: center;
          gap: 4px;
          box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);
          z-index: 1;
        }
        
        .improved-vision-tech-proof-item-details {
          padding: 16px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        
        .improved-vision-tech-proof-item-description {
          color: rgba(255, 255, 255, 0.9);
          font-size: 14px;
          line-height: 1.5;
          margin: 0 0 12px;
          flex: 1;
        }
        
        .improved-vision-tech-proof-item-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 12px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .improved-vision-tech-proof-item-phone {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
          margin: 0;
        }
        
        .improved-vision-tech-proof-item-date {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          background: rgba(255, 255, 255, 0.05);
          padding: 2px 8px;
          border-radius: 4px;
        }
        
        /* Floating Button */
        .improved-vision-tech-proof-floating-button {
          position: fixed;
          bottom: 80px;
          right: 20px;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(79, 70, 229, 0.5);
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
          font-size: 24px;
          font-weight: 600;
        }
        
        .improved-vision-tech-proof-floating-button:hover {
          transform: translateY(-3px) rotate(90deg);
          box-shadow: 0 8px 20px rgba(79, 70, 229, 0.6);
        }
        
        /* Share Proof View Styles */
        .improved-vision-tech-proof-share-header {
          display: flex;
          align-items: center;
          margin-bottom: 24px;
          gap: 16px;
        }
        
        .improved-vision-tech-proof-back-button {
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: rgba(255, 255, 255, 0.7);
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .improved-vision-tech-proof-back-button:hover {
          background: rgba(255, 255, 255, 0.15);
          color: #fff;
        }
        
        .improved-vision-tech-proof-share-title {
          font-size: 22px;
          font-weight: 600;
          margin: 0;
          background: linear-gradient(90deg, #fff, #a5b4fc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .improved-vision-tech-proof-share-instructions {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-left: 4px solid #4f46e5;
        }
        
        .improved-vision-tech-proof-instruction-icon {
          background: rgba(79, 70, 229, 0.2);
          color: #a5b4fc;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          flex-shrink: 0;
        }
        
        .improved-vision-tech-proof-share-instructions p {
          margin: 0;
          font-size: 14px;
          line-height: 1.5;
          color: rgba(255, 255, 255, 0.9);
        }
        
        .improved-vision-tech-proof-share-instructions strong {
          color: #a5b4fc;
        }
        
        /* Form Styles */
        .improved-vision-tech-proof-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .improved-vision-tech-proof-form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .improved-vision-tech-proof-form-group label {
          font-size: 14px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
        }
        
        .improved-vision-tech-proof-form-group input,
        .improved-vision-tech-proof-form-group textarea {
          background: rgba(255, 255, 255, 0.07);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          padding: 12px 16px;
          color: #fff;
          font-size: 14px;
          transition: all 0.2s ease;
          outline: none;
          width: 100%;
          box-sizing: border-box;
        }
        
        .improved-vision-tech-proof-form-group input:focus,
        .improved-vision-tech-proof-form-group textarea:focus {
          border-color: rgba(79, 70, 229, 0.5);
          background: rgba(255, 255, 255, 0.09);
          box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
        }
        
        .improved-vision-tech-proof-upload-area {
          margin-top: 10px;
        }
        
        .improved-vision-tech-proof-upload-label {
          display: block;
          cursor: pointer;
          width: 100%;
        }
        
        .improved-vision-tech-proof-upload-placeholder {
          border: 2px dashed rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          padding: 30px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          transition: all 0.3s ease;
          background: rgba(255, 255, 255, 0.03);
        }
        
        .improved-vision-tech-proof-upload-placeholder:hover {
          border-color: rgba(79, 70, 229, 0.5);
          background: rgba(255, 255, 255, 0.05);
        }
        
        .improved-vision-tech-proof-camera-icon {
          font-size: 30px;
          color: rgba(255, 255, 255, 0.5);
          margin-bottom: 12px;
        }
        
        .improved-vision-tech-proof-upload-placeholder p {
          font-size: 15px;
          font-weight: 500;
          margin: 0 0 8px;
          color: rgba(255, 255, 255, 0.8);
        }
        
        .improved-vision-tech-proof-upload-placeholder span {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
        }
        
        .improved-vision-tech-proof-preview {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        .improved-vision-tech-proof-preview img {
          width: 100%;
          display: block;
          max-height: 300px;
          object-fit: contain;
          background: rgba(0, 0, 0, 0.3);
        }
        
        .improved-vision-tech-proof-remove-image {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(255, 255, 255, 0.9);
          color: #ef4444;
          border: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .improved-vision-tech-proof-remove-image:hover {
          background: #fff;
          transform: scale(1.1);
        }
        
        .improved-vision-tech-proof-submit-button {
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          border: none;
          color: #fff;
          padding: 14px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .improved-vision-tech-proof-submit-button:hover:not(:disabled) {
          box-shadow: 0 4px 15px rgba(79, 70, 229, 0.4);
          transform: translateY(-2px);
        }
        
        .improved-vision-tech-proof-submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .improved-vision-tech-proof-spin-icon {
          animation: improved-vision-tech-proof-spin 1s linear infinite;
        }
        
        @media (min-width: 768px) {
          .improved-vision-tech-proof-list {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .improved-vision-tech-proof-form {
            max-width: 600px;
            margin: 0 auto;
          }
        }
        
        @media (max-width: 767px) {
          .improved-vision-tech-proof-container {
            padding: 16px 12px;
          }
          
          .improved-vision-tech-proof-title {
            font-size: 22px;
          }
          
          .improved-vision-tech-proof-action-bar {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          
          .improved-vision-tech-proof-counter {
            align-self: flex-end;
          }
          
          .improved-vision-tech-proof-share-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Proof;